// src/pages/PublicSharedCollection.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
   Box,
   Typography,
   Button,
   CircularProgress,
   Alert,
   Container,
   Chip,
   Dialog,
   DialogContent,
   DialogTitle,
   DialogActions,
   IconButton,
   TextField,
   Avatar,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
   Close as CloseIcon,
   Email as EmailIcon,
   Favorite as FavoriteIcon,
   Visibility as VisibilityIcon,
   PhotoLibrary as PhotoLibraryIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { SharedCollection, SharedCollectionPublicComment } from '../types/SharedCollection';
import SharedCollectionService from '../services/sharedCollectionService';
import { useUser } from '../contexts/UserContext';
import {
   filterSharedPhotosByQueryIds,
   parsePhotosQueryString,
} from '../utils/sharedCollectionPhotosQuery';
import {
   getCombinedViewCount,
   getTotalCollectionLikes,
} from '../utils/sharedCollectionPublicStats';
import {
   countVideosInPhotos,
   isSharedVideoPhoto,
   videoPlaybackSrc,
} from '../utils/sharedCollectionMedia';
import SharedCollectionPhotoGrid from '../components/sharedCollection/SharedCollectionPhotoGrid';
import SharedCollectionPublicCommentsPanel from '../components/sharedCollection/SharedCollectionPublicCommentsPanel';

const PublicSharedCollection: React.FC = () => {
   const { shareToken } = useParams<{ shareToken: string }>();
   const navigate = useNavigate();
   const [searchParams] = useSearchParams();
   const photosQueryParam = searchParams.get('photos');
   const { currentUser, isLoading: userLoading } = useUser();

   // Extract email from URL parameters
   const urlParams = new URLSearchParams(window.location.search);
   const emailFromUrl = urlParams.get('email');
   const [collection, setCollection] = useState<SharedCollection | null>(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
   const [hasViewedBefore, setHasViewedBefore] = useState(false);
   const [showEmailDialog, setShowEmailDialog] = useState(false);
   const [viewerEmail, setViewerEmail] = useState('');
   const [emailError, setEmailError] = useState('');
   const [activePhotoOriginalId, setActivePhotoOriginalId] = useState<string | null>(null);
   const [focusComposerSignal, setFocusComposerSignal] = useState(0);
   const [commentCountByPhotoId, setCommentCountByPhotoId] = useState<Record<string, number>>({});

   const { displayPhotos, missingIds } = useMemo(
      () =>
         filterSharedPhotosByQueryIds(
            collection?.photos ?? [],
            parsePhotosQueryString(photosQueryParam),
         ),
      [collection, photosQueryParam],
   );

   const handleOpenPhotoByOriginalId = useCallback(
      (originalPhotoId: string) => {
         const id = String(originalPhotoId || '').trim();
         if (!id) return;
         const idx = displayPhotos.findIndex((p) => p.originalPhotoId === id);
         if (idx >= 0) setSelectedPhotoIndex(idx);
      },
      [displayPhotos],
   );

   /** Full-collection totals (not `?photos=` subset). */
   const totalLikesFullCollection = useMemo(
      () => (collection ? getTotalCollectionLikes(collection.photos) : 0),
      [collection],
   );

   const displayVideoCount = useMemo(
      () => countVideosInPhotos(displayPhotos),
      [displayPhotos],
   );
   const mediaCountChipLabel =
      displayVideoCount > 0
         ? `${displayPhotos.length} items`
         : `${displayPhotos.length} photos`;

   useEffect(() => {
      if (!shareToken) return;
      if (userLoading) return;

      if (emailFromUrl) {
         fetchCollection(emailFromUrl);
      } else {
         fetchCollection();
      }
   }, [shareToken, currentUser, userLoading, emailFromUrl]);

   useEffect(() => {
      // Update recipient status when user views the collection
      if (collection && currentUser && !hasViewedBefore) {
         updateRecipientStatus('viewed');
         setHasViewedBefore(true);
      }
   }, [collection, currentUser, hasViewedBefore]);

   const fetchCollection = async (email?: string) => {
      try {
         setLoading(true);

         // Determine viewer email
         let viewerEmailToUse: string | undefined = email || viewerEmail;
         if (currentUser && currentUser.email) {
            viewerEmailToUse = currentUser.email;
         }

         // Only pass email if it's not empty
         const emailToPass =
            viewerEmailToUse && viewerEmailToUse.trim() ? viewerEmailToUse : undefined;

         console.log('🚀 Fetching collection with:', {
            shareToken,
            emailToPass,
            currentUser: !!currentUser,
            fullUrl: window.location.href,
         });

         const sharedCollection = await SharedCollectionService.getSharedCollectionByToken(
            shareToken!,
            emailToPass
         );

         if (!sharedCollection) {
            setError('Shared collection not found or has been removed.');
            return;
         }

         console.log('PublicSharedCollection - Fetched collection:', sharedCollection);
         console.log(
            'PublicSharedCollection - Photos with like data:',
            sharedCollection.photos?.map((p) => ({
               id: p.originalPhotoId,
               likeCount: p.likeCount,
               isLiked: p.isLiked,
            }))
         );
         setCollection(sharedCollection);
      } catch (error) {
         console.error('Error fetching shared collection:', error);
         setError('Failed to load shared collection');
      } finally {
         setLoading(false);
      }
   };

   const updateRecipientStatus = async (status: 'viewed') => {
      if (!collection || !currentUser) return;

      try {
         await SharedCollectionService.updateRecipientStatus(
            collection.id,
            currentUser.email!,
            status,
            currentUser.uid
         );
      } catch (error) {
         console.error('Error updating recipient status:', error);
      }
   };

   const handleJoinWetrippen = () => {
      navigate('/signup', {
         state: {
            invitedViaSharing: true,
            shareToken: shareToken,
            returnTo: shareToken ? `/shared/collection/${shareToken}` : undefined,
         },
      });
   };

   const handleLogin = () => {
      navigate('/login', {
         state: {
            returnTo: `/shared/collection/${shareToken}`,
         },
      });
   };

   const validateEmail = (email: string): boolean => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
   };

   const checkUserExistsAndRedirect = async (email: string) => {
      try {
         // Check if user exists using the auth service
         const response = await fetch(
            `${process.env.REACT_APP_API_URL || 'http://localhost:4001'}/api/auth/check-user-exists`,
            {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({ email }),
            }
         );

         const data = await response.json();

         if (data.exists) {
            // User exists but not logged in - redirect to login
            console.log('User exists, redirecting to login...');
            navigate('/login', {
               state: {
                  returnTo: `/shared/collection/${shareToken}`,
               },
            });
         } else {
            // User doesn't exist - redirect to register
            console.log('User does not exist, redirecting to register...');
            navigate('/register', {
               state: {
                  returnTo: `/shared/collection/${shareToken}`,
                  invitedViaSharing: true,
                  shareToken: shareToken,
               },
            });
         }
      } catch (error) {
         console.error('Error checking user existence:', error);
         // Default to register if we can't check
         navigate('/register', {
            state: {
               returnTo: `/shared/collection/${shareToken}`,
               invitedViaSharing: true,
               shareToken: shareToken,
            },
         });
      }
   };

   const handleEmailSubmit = async () => {
      setEmailError('');

      if (!viewerEmail.trim()) {
         setEmailError('Please enter your email address');
         return;
      }

      if (!validateEmail(viewerEmail)) {
         setEmailError('Please enter a valid email address');
         return;
      }

      setShowEmailDialog(false);
      setLoading(true);
      await checkUserExistsAndRedirect(viewerEmail);
   };

   const handleSkipEmail = () => {
      // If they skip, just redirect to register as a new user
      navigate('/register', {
         state: {
            returnTo: `/shared/collection/${shareToken}`,
            invitedViaSharing: true,
            shareToken: shareToken,
         },
      });
   };

   const handleSharedPhotoLikeChange = (
      originalPhotoId: string,
      isLiked: boolean,
      likeCount: number
   ) => {
      setCollection((prev) => {
         if (!prev) return prev;
         const updatedPhotos = prev.photos.map((p) =>
            p.originalPhotoId === originalPhotoId ? { ...p, isLiked, likeCount } : p
         );
         return { ...prev, photos: updatedPhotos };
      });
   };

   const handleCommentsChange = useCallback((list: SharedCollectionPublicComment[]) => {
      const m: Record<string, number> = {};
      list.forEach((c) => {
         if (c.parentCommentId) return;
         if (c.photoOriginalId) {
            m[c.photoOriginalId] = (m[c.photoOriginalId] || 0) + 1;
         }
      });
      setCommentCountByPhotoId(m);
   }, []);

   const handlePhotoCommentClick = useCallback(
      (index: number) => {
         const photo = displayPhotos[index];
         if (!photo) return;
         setActivePhotoOriginalId(photo.originalPhotoId);
         setFocusComposerSignal((n) => n + 1);
      },
      [displayPhotos],
   );

   const getEntityTypeIcon = (entityType: string) => {
      switch (entityType) {
         case 'trip':
            return '✈️';
         case 'event':
            return '📅';
         case 'album':
            return '📸';
         default:
            return '📁';
      }
   };

   if (loading) {
      return (
         <Container maxWidth='lg'>
            <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
               <CircularProgress />
            </Box>
         </Container>
      );
   }

   if (error) {
      return (
         <Container maxWidth='lg'>
            <Box p={3}>
               <Alert severity='error'>{error}</Alert>
               <Box mt={2} textAlign='center'>
                  <Button variant='contained' onClick={() => navigate('/')}>
                     Go to Home
                  </Button>
               </Box>
            </Box>
         </Container>
      );
   }

   if (!collection) {
      return (
         <Container maxWidth='lg'>
            <Box p={3}>
               <Alert severity='warning'>Collection not found</Alert>
            </Box>
         </Container>
      );
   }

   return (
      <Container maxWidth='xl' sx={{ pb: 2 }}>
         <Box py={2}>
            <Box
               sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 2,
                  alignItems: 'flex-start',
               }}
            >
               {/* Left column: collection header + photos */}
               <Box
                  sx={(t) => ({
                     flex: 1,
                     minWidth: 0,
                     width: { xs: '100%', md: 'auto' },
                     boxSizing: 'border-box',
                     borderRight: {
                        xs: 'none',
                        md: `1px solid ${
                           t.palette.mode === 'dark'
                              ? alpha(t.palette.common.white, 0.08)
                              : alpha(t.palette.common.black, 0.08)
                        }`,
                     },
                     pr: { xs: 0, md: 2 },
                  })}
               >
                  <Box
                     sx={{
                        p: { xs: 2, sm: 3 },
                        mb: 3,
                        borderRadius: 0,
                        border: 'none',
                        bgcolor: 'transparent',
                     }}
                  >
                     <Box
                        sx={(t) => ({
                           height: 2,
                           borderRadius: 999,
                           mb: 2.5,
                           bgcolor: alpha(t.palette.primary.main, 0.4),
                        })}
                     />
                     <Box display='flex' alignItems='flex-start' gap={2}>
                  <Avatar
                     src={collection.creatorPhotoURL || undefined}
                     alt=''
                           sx={(t) => ({
                              width: 52,
                              height: 52,
                              fontSize: '1.1rem',
                              fontWeight: 700,
                              bgcolor: 'action.hover',
                              color: 'text.secondary',
                           })}
                  >
                     {(
                        collection.creatorDisplayName ||
                        collection.senderName ||
                        'S'
                     ).charAt(0).toUpperCase()}
                  </Avatar>
                        <Box flex={1} minWidth={0}>
                           <Typography
                              variant='overline'
                              sx={{
                                 display: 'block',
                                 letterSpacing: '0.14em',
                                 fontWeight: 700,
                                 color: 'text.secondary',
                                 mb: 0.5,
                              }}
                           >
                              Shared collection
                           </Typography>
                           <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.5 }}>
                     Shared by{' '}
                              <Box component='span' fontWeight={700} color='text.primary'>
                        {collection.creatorDisplayName || collection.senderName || 'Someone'}
                     </Box>
                  </Typography>
               </Box>
                     </Box>

                     <Box display='flex' alignItems='flex-start' gap={1.75} mt={3} mb={1}>
                        <Box
                           sx={(t) => ({
                              width: 52,
                              height: 52,
                              borderRadius: 1.5,
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '1.6rem',
                              lineHeight: 1,
                              bgcolor: alpha(t.palette.common.white, 0.06),
                              color: 'text.secondary',
                           })}
                        >
                     {getEntityTypeIcon(collection.entityType)}
                        </Box>
                        <Typography
                           component='h1'
                           variant='h4'
                           sx={{
                              fontWeight: 600,
                              letterSpacing: '-0.02em',
                              lineHeight: 1.25,
                              pt: 0.25,
                           }}
                        >
                     {collection.title ||
                        collection.subject ||
                        `Photos from ${collection.entityTitle}`}
                  </Typography>
               </Box>

                     <Typography variant='body2' color='text.secondary' sx={{ mt: 1, mb: 0 }}>
                        Shared on{' '}
                        <Box component='span' color='text.primary' fontWeight={500}>
                           {format(collection.createdAt, 'MMMM d, yyyy')}
                        </Box>
               </Typography>

               {collection.message && (
                        <Box
                           mt={2.5}
                           p={2}
                           sx={(t) => ({
                              borderRadius: 1,
                              bgcolor: alpha(t.palette.common.white, 0.04),
                           })}
                        >
                           <Typography variant='caption' color='text.secondary' fontWeight={700} sx={{ display: 'block', mb: 1 }}>
                              Note from sender
                           </Typography>
                           <Typography variant='body1' sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                        {collection.message}
                     </Typography>
                  </Box>
               )}

               {photosQueryParam && missingIds.length > 0 && (
                        <Alert severity='info' sx={{ mt: 2, borderRadius: 1 }}>
                     Some photo links are no longer in this collection.
                  </Alert>
               )}

                     <Typography
                        variant='caption'
                        color='text.secondary'
                        fontWeight={700}
                        sx={{ display: 'block', letterSpacing: '0.12em', mt: 3, mb: 1.25 }}
                     >
                        Activity
                     </Typography>
                     <Box display='flex' gap={1} alignItems='center' flexWrap='wrap'>
                  <Chip
                     icon={<PhotoLibraryIcon />}
                     label={mediaCountChipLabel}
                           variant='filled'
                     size='small'
                           sx={(t) => ({
                              bgcolor: alpha(t.palette.common.white, 0.08),
                              color: 'text.secondary',
                              fontWeight: 600,
                              border: 'none',
                              '& .MuiChip-icon': { color: 'text.secondary' },
                              '&:hover': { bgcolor: alpha(t.palette.common.white, 0.12) },
                           })}
                  />
                  <Chip
                     icon={<VisibilityIcon />}
                     label={`${getCombinedViewCount(collection)} views`}
                           variant='filled'
                     size='small'
                           sx={(t) => ({
                              bgcolor: alpha(t.palette.common.white, 0.08),
                              color: 'text.secondary',
                              border: 'none',
                              '& .MuiChip-icon': { color: 'text.secondary' },
                              '&:hover': { bgcolor: alpha(t.palette.common.white, 0.12) },
                           })}
                  />
                  <Chip
                     icon={<FavoriteIcon />}
                     label={`${totalLikesFullCollection} likes`}
                           variant='filled'
                     size='small'
                           sx={(t) => ({
                              bgcolor: alpha(t.palette.common.white, 0.08),
                              color: 'text.secondary',
                              border: 'none',
                              '& .MuiChip-icon': { color: 'text.secondary' },
                              '&:hover': { bgcolor: alpha(t.palette.common.white, 0.12) },
                           })}
                  />
               </Box>
                  </Box>

            {photosQueryParam && displayPhotos.length === 0 && collection.photos.length > 0 && (
                     <Alert severity='warning' sx={{ mb: 2, borderRadius: 1 }}>
                  None of the photos in this link are available in the collection.
               </Alert>
            )}

               {collection && displayPhotos.length > 0 && (
                     <Box
                        sx={{
                           p: { xs: 2, sm: 2.5 },
                           pt: { xs: 1, sm: 1.5 },
                           border: 'none',
                           bgcolor: 'transparent',
                        }}
                     >
                        <Typography
                           variant='overline'
                           sx={{
                              letterSpacing: '0.14em',
                              fontWeight: 700,
                              color: 'text.secondary',
                              display: 'block',
                              mb: 2,
                           }}
                        >
                           Gallery
                        </Typography>
                  <SharedCollectionPhotoGrid
                     collection={collection}
                     photos={displayPhotos}
                     currentUserId={currentUser?.uid || ''}
                     likeButtonsDisabled={!currentUser?.uid}
                     onPhotoClick={setSelectedPhotoIndex}
                     onPhotoLikeChange={handleSharedPhotoLikeChange}
                           commentCountByPhotoId={commentCountByPhotoId}
                           onPhotoCommentClick={handlePhotoCommentClick}
                        />
                     </Box>
                  )}
               </Box>

               {/* Right column: fixed-size comments sidebar */}
               {shareToken && (
                  <Box
                     sx={(t) => ({
                        width: { xs: '100%', md: 400 },
                        height: { xs: 520, md: 720 },
                        maxHeight: { md: 'calc(100vh - 72px)' },
                        flexShrink: 0,
                        position: { md: 'sticky' },
                        top: { md: 16 },
                        alignSelf: 'flex-start',
                        boxSizing: 'border-box',
                        borderTop: {
                           xs: `1px solid ${
                              t.palette.mode === 'dark'
                                 ? alpha(t.palette.common.white, 0.08)
                                 : alpha(t.palette.common.black, 0.08)
                           }`,
                           md: 'none',
                        },
                        pt: { xs: 2, md: 0 },
                     })}
                  >
                     <SharedCollectionPublicCommentsPanel
                        shareToken={shareToken}
                        onOpenTaggedPhoto={handleOpenPhotoByOriginalId}
                        currentUser={
                           currentUser
                              ? {
                                   uid: currentUser.uid,
                                   displayName: currentUser.displayName,
                                   photoURL: currentUser.photoURL,
                                   email: currentUser.email,
                                }
                              : null
                        }
                        displayPhotos={displayPhotos}
                        activePhotoOriginalId={activePhotoOriginalId}
                        onActivePhotoOriginalIdChange={setActivePhotoOriginalId}
                        focusComposerSignal={focusComposerSignal}
                        onNavigateLogin={handleLogin}
                        onNavigateRegister={handleJoinWetrippen}
                        onCommentsChange={handleCommentsChange}
                     />
                  </Box>
               )}
            </Box>

            {/* Photo Lightbox */}
            <Dialog
               open={selectedPhotoIndex !== null}
               onClose={() => setSelectedPhotoIndex(null)}
               maxWidth='lg'
               fullWidth
            >
               <DialogContent sx={{ p: 0, position: 'relative' }}>
                  {selectedPhotoIndex !== null && displayPhotos[selectedPhotoIndex] && (
                     <>
                        <IconButton
                           sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'rgba(0,0,0,0.5)',
                              color: 'white',
                              zIndex: 1,
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                           }}
                           onClick={() => setSelectedPhotoIndex(null)}
                        >
                           <CloseIcon />
                        </IconButton>
                        {isSharedVideoPhoto(displayPhotos[selectedPhotoIndex]) ? (
                           <Box
                              component='video'
                              src={videoPlaybackSrc(displayPhotos[selectedPhotoIndex])}
                              controls
                              playsInline
                              sx={{
                                 width: '100%',
                                 height: 'auto',
                                 maxHeight: '90vh',
                                 objectFit: 'contain',
                                 display: 'block',
                                 bgcolor: 'black',
                              }}
                           />
                        ) : (
                           <Box
                              component='img'
                              src={displayPhotos[selectedPhotoIndex].url}
                              alt={displayPhotos[selectedPhotoIndex].title || ''}
                              sx={{
                                 width: '100%',
                                 height: 'auto',
                                 maxHeight: '90vh',
                                 objectFit: 'contain',
                                 display: 'block',
                              }}
                           />
                        )}
                        {displayPhotos[selectedPhotoIndex].title && (
                           <Box p={2}>
                              <Typography variant='h6'>
                                 {displayPhotos[selectedPhotoIndex].title}
                              </Typography>
                              {displayPhotos[selectedPhotoIndex].description && (
                                 <Typography variant='body2' color='textSecondary'>
                                    {displayPhotos[selectedPhotoIndex].description}
                                 </Typography>
                              )}
                           </Box>
                        )}
                     </>
                  )}
               </DialogContent>
            </Dialog>

            {/* Email Collection Dialog */}
            <Dialog
               open={showEmailDialog}
               onClose={() => {}}
               maxWidth='sm'
               fullWidth
               disableEscapeKeyDown
               aria-labelledby='email-dialog-title'
            >
               <DialogTitle id='email-dialog-title'>
                  <Box display='flex' alignItems='center' gap={1}>
                     <EmailIcon color='primary' />
                     <Typography variant='h6'>Access Required</Typography>
                  </Box>
               </DialogTitle>
               <DialogContent>
                  <Typography variant='body1' gutterBottom>
                     To view this photo collection, please enter your email address.
                  </Typography>
                  <Typography variant='body2' color='textSecondary' gutterBottom sx={{ mb: 3 }}>
                     We'll check if you have an account and redirect you to either login or
                     register.
                  </Typography>
                  <TextField
                     autoFocus
                     margin='dense'
                     label='Email Address'
                     type='email'
                     fullWidth
                     variant='outlined'
                     value={viewerEmail}
                     onChange={(e) => setViewerEmail(e.target.value)}
                     onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                           handleEmailSubmit();
                        }
                     }}
                     error={!!emailError}
                     helperText={emailError}
                     placeholder='your.email@example.com'
                  />
               </DialogContent>
               <DialogActions sx={{ px: 3, pb: 2 }}>
                  <Button onClick={handleSkipEmail} color='inherit'>
                     I'm New Here
                  </Button>
                  <Button
                     onClick={handleEmailSubmit}
                     variant='contained'
                     disabled={!viewerEmail.trim()}
                  >
                     Continue
                  </Button>
               </DialogActions>
            </Dialog>

         </Box>
      </Container>
   );
};

export default PublicSharedCollection;
